import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiAlertCircle, FiTrash2 } from "react-icons/fi";
import {
  GetMediaDocument,
  GetTrashedMediaDocument,
  type GetTrashedMediaQuery,
  type GetTrashedMediaQueryVariables,
  type MediaType,
  PermanentlyDeleteDocument,
  type PermanentlyDeleteMutation,
  type PermanentlyDeleteMutationVariables,
  RestoreFromTrashDocument,
  type RestoreFromTrashMutation,
  type RestoreFromTrashMutationVariables,
} from "../../generated/graphql";
import { ConfirmationModal } from "./modals";
import { EmptyState, TrashHeader, TrashTable } from "./shared";
import type { TrashTableColumn } from "./shared";

interface TrashViewProps {
  folderId: string | null;
  mediaType: MediaType;
}

const columns: TrashTableColumn[] = [
  { header: "File" },
  { header: "Deleted At" },
  { header: "Size" },
];

export function TrashView({ folderId, mediaType }: TrashViewProps) {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<string[]>([]);
  const [isDeletingPermanently, setIsDeletingPermanently] = useState(false);

  // Helper to get media route based on type
  const getMediaRoute = (type: MediaType): string => {
    const typeMap: Record<MediaType, string> = {
      audio: "/media/audio",
      image: "/media/images",
      video: "/media/videos",
      file: "/media/files",
    };
    return typeMap[type];
  };

  // get readable media type label
  const getMediaTypeLabel = (type: MediaType): string => {
    const labelMap: Record<MediaType, string> = {
      audio: "Audio",
      image: "Images",
      video: "Videos",
      file: "Files",
    };
    return labelMap[type];
  };

  // Query trashed media for this folder (or root if folderId is null)
  const { data, loading } = useQuery<GetTrashedMediaQuery, GetTrashedMediaQueryVariables>(
    GetTrashedMediaDocument,
    {
      variables: { type: mediaType, folderId },
    }
  );

  // Restore mutation
  const [restoreFromTrash, { loading: restoring }] = useMutation<
    RestoreFromTrashMutation,
    RestoreFromTrashMutationVariables
  >(RestoreFromTrashDocument, {
    refetchQueries: [
      {
        query: GetTrashedMediaDocument,
        variables: { type: mediaType, folderId },
      },
      {
        query: GetMediaDocument,
        variables: { folderId: folderId || undefined, type: mediaType },
      },
    ],
    onCompleted: () => {
      toast.success("Media restored successfully");
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`);
    },
  });

  // Permanent delete mutation
  const [permanentlyDelete, { loading: deleting }] = useMutation<
    PermanentlyDeleteMutation,
    PermanentlyDeleteMutationVariables
  >(PermanentlyDeleteDocument, {
    refetchQueries: [
      {
        query: GetTrashedMediaDocument,
        variables: { type: mediaType, folderId },
      },
    ],
    onCompleted: () => {
      toast.success("Media permanently deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const trashedMedia = data?.trashedMedia?.edges?.map((e) => e.node) || [];

  const handleToggleSelect = (id: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === trashedMedia.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(trashedMedia.map((item) => item.id)));
    }
  };

  const handleRestore = async (id: string) => {
    await restoreFromTrash({ variables: { id } });
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const handlePermanentDelete = (id: string) => {
    setItemsToDelete([id]);
    setShowDeleteConfirm(true);
  };

  const handleConfirmPermanentDelete = async () => {
    if (itemsToDelete.length === 0) return;

    setIsDeletingPermanently(true);
    try {
      for (const id of itemsToDelete) {
        await permanentlyDelete({ variables: { id } });
        setSelectedItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    } catch (_error) {
      // Error already handled by mutation
    } finally {
      setIsDeletingPermanently(false);
      setShowDeleteConfirm(false);
      setItemsToDelete([]);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.size === 0) return;

    try {
      for (const id of selectedItems) {
        await restoreFromTrash({ variables: { id } });
      }
      setSelectedItems(new Set());
    } catch (_error) {
      // Error already handled by mutation
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    setItemsToDelete(Array.from(selectedItems));
    setShowDeleteConfirm(true);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit" as const,
      minute: "2-digit" as const,
    });
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2);
  };

  const isProcessing = restoring || deleting;

  return (
    <div className="flex h-full flex-col">
      {/* header with back button and bulk actions */}
      <TrashHeader
        title="Trash"
        itemCount={trashedMedia.length}
        selectedCount={selectedItems.size}
        backLabel={`Back to ${getMediaTypeLabel(mediaType)}`}
        onBack={() => navigate({ to: getMediaRoute(mediaType) as string })}
        onBulkRestore={handleBulkRestore}
        onBulkDelete={handleBulkDelete}
        isProcessing={isProcessing}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {/* Empty State */}
        {!loading && trashedMedia.length === 0 && (
          <EmptyState
            icon={FiTrash2}
            title="Trash is empty"
            description="No deleted items in this folder"
          />
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading trashed media...</div>
          </div>
        )}

        {/* Table */}
        {!loading && trashedMedia.length > 0 && (
          <TrashTable
            items={trashedMedia}
            columns={columns}
            selectedItems={selectedItems}
            getItemId={(item) => item.id}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onRestore={handleRestore}
            onDelete={handlePermanentDelete}
            isProcessing={isProcessing}
            renderRow={(item) => (
              <>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FiAlertCircle className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{item.filename}</p>
                      {item.tags && item.tags.length > 0 && (
                        <p className="text-xs text-gray-500">{item.tags.join(", ")}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.deletedAt ? formatDate(item.deletedAt) : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatFileSize(item.size)} MB
                </td>
              </>
            )}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemsToDelete([]);
        }}
        onConfirm={handleConfirmPermanentDelete}
        title="Permanently Delete"
        message={
          itemsToDelete.length === 1
            ? "Permanently delete this item? This cannot be undone."
            : `Permanently delete ${itemsToDelete.length} items? This cannot be undone.`
        }
        confirmLabel="Delete Permanently"
        variant="danger"
        isLoading={isDeletingPermanently}
      />
    </div>
  );
}
